import React, { useCallback, useState } from 'react';
import { buildInitialSignersFormData, ISignersFormData } from './buildInitialSignersFormData';
import { useBuildSignersValidationSchema } from './signersValidationSchema';
import { FieldArray, Form, Formik } from 'formik';
import { AddSignerRows } from './AddSignerRows';
import { AddSignerButtons } from './AddSignerButtons';
import { WizardFormProps } from '../../shared/WizardFormProps';
import { useEmailRequired } from '../hooks/useEmailRequired';
import { ArrayLevelValidationMessage } from '../../../../../shared/ArrayLevelValidationMessage';

export enum FormTypes {
  New = 'new',
  Current = 'Current',
}
export const SignersForm: React.FC<WizardFormProps> = ({
  advance,
  updatedWizardFormData,
  setUpdatedWizardFormData,
  children,
}) => {
  const emailRequired = useEmailRequired(updatedWizardFormData);
  const validationSchema = useBuildSignersValidationSchema(emailRequired);
  const [typesIndices, setTypesIndices] = useState<FormTypes[]>([]);

  const handleOnTrashIconClick = useCallback(
    (index: number) => {
      const typesCopy = typesIndices.slice();
      typesCopy.splice(index, 1);

      setTypesIndices([...typesCopy]);
      setUpdatedWizardFormData({
        ...updatedWizardFormData,
        signers: undefined,
      });
    },
    [typesIndices, setTypesIndices, setUpdatedWizardFormData, updatedWizardFormData]
  );

  const onAddSignerClick = useCallback(
    (type: FormTypes) => {
      setTypesIndices([...typesIndices, type]);
    },
    [typesIndices, setTypesIndices]
  );

  return (
    <Formik
      initialValues={buildInitialSignersFormData(updatedWizardFormData)}
      validationSchema={validationSchema}
      onSubmit={({ signers }: ISignersFormData) => {
        setUpdatedWizardFormData((prevState) => ({
          ...prevState,
          signers,
        }));
        advance();
      }}
      validateOnMount
    >
      <Form>
        <ArrayLevelValidationMessage name={'signers'} />
        <FieldArray name="signers">
          {({ push, remove, replace }) => (
            <>
              <AddSignerRows
                handleOnTrashIconClick={handleOnTrashIconClick}
                remove={remove}
                typesIndices={typesIndices}
                ownerType={updatedWizardFormData.ownerType}
                replace={replace}
                emailRequired={emailRequired}
              />
              <AddSignerButtons push={push} onAddSignerClick={onAddSignerClick} />
            </>
          )}
        </FieldArray>
        {children}
      </Form>
    </Formik>
  );
};
